import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  clearCategoryError,
} from "./../store/features/category/category";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import SearchBar from "./../components/common/SearchBar"; // Import the SearchBar component
import SnackNotification from "../components/common/SnackNotification";


const Category = () => {
  const dispatch = useDispatch();

  const { categories, loading, error } = useSelector((state) => state.category);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);

  // State for searched categories
  const [searchedCategories, setSearchedCategories] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (categories) {
        setSearchedCategories(categories);
    }
  }, [categories]); // Update searchedCategories when categories change

  useEffect(() => {
    if (error) {
      console.error("Category Error:", error);
      dispatch(clearCategoryError());
    }
  }, [error, dispatch]);

  const onSubmit = async (data) => {
     if(data.label.length<10){
      setSnackbarMessage("Catagory name should contain atleast 10 character ");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return
    }
    if (!/^[A-Za-z\s]+$/.test(data.label)) {
      setSnackbarMessage("Catagory name should contain only alphabets");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    const payload = {
      label: data.label.trim(),
      description: data.description.trim(),
    };

    try {
      if (editingId) {
        await dispatch(updateCategory({ id: editingId, data: payload })).unwrap();
      } else {
        await dispatch(createCategory(payload)).unwrap();
      }
      reset();
      setEditingId(null);
      setOpenDialog(false);
      setPage(0);
    } catch (err) {
      console.error("Submit Error:", err);
    }
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEdit = (index) => {
    const cat = searchedCategories[page * rowsPerPage + index];
    setValue("label", cat.label);
    setValue("description", cat.description);
    setEditingId(cat._id);
    setOpenDialog(true);
  };

  const handleDelete = async (index) => {
    const cat = searchedCategories[page * rowsPerPage + index];
    try {
      await dispatch(deleteCategory(cat._id)).unwrap();
      if (editingId === cat._id) {
        reset();
        setEditingId(null);
        setOpenDialog(false);
      }
    } catch (err) {
      console.error("Delete Error:", err);
    }
  };

  const handleCloseDialog = () => {
    reset();
    setEditingId(null);
    setOpenDialog(false);
  };

  const handleSearch = (filteredData) => {
    if (filteredData.length === categories.length && JSON.stringify(filteredData) === JSON.stringify(categories)) {
        setIsSearching(false);
    } else {
        setIsSearching(true);
    }
    setSearchedCategories(filteredData);
    setPage(0); // Reset to the first page on search
  };


  return (
    <Stack spacing={4}>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#212529",
            color: "#fff",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #343a40", fontWeight: "bold" }}>
          {editingId ? "Edit Category" : "Create Category"}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
            <Stack spacing={3}>
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: "#adb5bd" }}>
                  Category Name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("label", { required: "Category name is required" })}
                  error={!!errors.label}
                  helperText={errors.label?.message}
                  sx={{
                    bgcolor: "#343a40",
                    input: { color: "#fff" },
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6c757d" },
                      "&:hover fieldset": { borderColor: "#adb5bd" },
                      "&.Mui-focused fieldset": { borderColor: "#90caf9" },
                    },
                    "& .MuiFormHelperText-root": { color: "#f44336" },
                  }}
                />
              </div>

              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: "#adb5bd" }}>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                  {...register("description")}
                  sx={{
                    bgcolor: "#343a40",
                    textarea: { color: "#fff" },
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6c757d" },
                      "&:hover fieldset": { borderColor: "#adb5bd" },
                      "&.Mui-focused fieldset": { borderColor: "#90caf9" },
                    },
                  }}
                />
              </div>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit" sx={{ color: "#adb5bd" }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ textTransform: "none", bgcolor: "#1976d2" }}
            >
              {editingId ? "Update Category" : "Create Category"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Paper elevation={3} sx={{ bgcolor: "#1e1e1e", p: 2, borderRadius: 2, color: "#fff" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap={"wrap"}
          mb={3}
          
          >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: "bold" }}>
            Category List
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap={"wrap"}> {/* Added Box for SearchBar and Button */}
          
            <SearchBar
              data={categories} // Pass the original categories for searching
              searchKeys={["label", "description"]}
              onSearch={handleSearch}
              placeholder="Search categories..."
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingId(null);
                reset();
                setOpenDialog(true);
              }}
              sx={{
                textTransform: "none",
                bgcolor: "#1976d2",
                fontWeight: "bold",
              }}
            >
              Create Category
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Typography variant="body2" sx={{ p: 3, color: "#adb5bd" }}>
            Loading categories...
          </Typography>
        ) : searchedCategories.length > 0 ? ( // Use searchedCategories here
          <>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ backgroundColor: "transparent", maxHeight: 440 }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#8d8787" }}>
                    <TableCell sx={{ fontWeight: "bolder" }}>Sno</TableCell>
                    <TableCell sx={{ fontWeight: "bolder" }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: "bolder" }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: "bolder" }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchedCategories
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((cat, index) => (
                      <TableRow key={cat._id} hover>
                        <TableCell sx={{ color: "white", py: "5px" }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ color: "white", py: "5px" }}>{cat.label}</TableCell>
                        <TableCell sx={{ color: "white", py: "5px" }}>
                          {cat.description || "—"}
                        </TableCell>
                        <TableCell sx={{ color: "white", py: "5px" }} align="center">
                          <IconButton
                            sx={{ py: "5px" }}
                            color="success"
                            onClick={() => handleEdit(index)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton sx={{ py: "5px" }} onClick={() => handleDelete(index)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              className="mui-wrapper mui-isolated-container p-0"
              sx={{
                color: "white",
                px: 2,
                py: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #ccc'
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={searchedCategories.length} // Use searchedCategories length for pagination
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  background: "#8d8787",
                  color: "white",
                  ".MuiTablePagination-toolbar": {
                    
                    color: "white",
                  },
                       '.MuiTablePagination-toolbar': { color: 'white',overflow:{
                    xs:"scroll" ,
                    sm:"hidden" 
                  } },
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    color: "white",
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 3 }}>
            {isSearching ? "No matching categories found." : "No categories added yet."}
          </Typography>
        )}
      </Paper>
      <SnackNotification
            open={snackbarOpen}
            message={snackbarMessage}
            severity={snackbarSeverity}
            handleClose={handleSnackbarClose}
            />
    </Stack>
  );
};

export default Category;